'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import cx from 'classnames';

type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge';
type ButtonVariant = 'default' | 'basic' | 'outline';

interface ButtonProps {
    className?: string;
    label: React.ReactNode;
    size?: ButtonSize;
    variant?: ButtonVariant;
    animated?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    href?: string;
}

const Button = ({ label, onClick, size = 'medium', variant = 'default', animated = false, disabled = false, className = '', href }: ButtonProps) => {
    const motionButton: any = {
        init: {},
        hover: {
            scale: size === 'large' || size === 'xlarge' ? 1.1 : 1.05
        },
        transition: {
            type: 'spring',
            stiffness: 400,
            damping: 10
        }
    };

    const motionLabel: any = {
        init: {
            translateX: '16px'
        },
        hover: {
            translateX: '0px'
        }
    };

    const motionArrow: any = {
        init: {
            translateX: '-12px',
            opacity: 0
        },
        hover: {
            translateX: '0px',
            opacity: 1,
            transition: { delay: 0.19 }
        }
    };

    if (! animated) {
        motionLabel.init = { translateX: '0px' };

        delete motionButton.hover;
        delete motionLabel.hover;
        delete motionArrow.hover;
    }

    const render = (
        <motion.button
            whileHover='hover'
            initial='init'
            variants={motionButton}
            className={cx('rounded-full border-2 font-medium flex justify-center items-center gap-2 transition-colors', {
                'bg-black text-white border-black hover:border-neutral-800 hover:bg-neutral-800': ! disabled && variant === 'default',
                'text-gray-600 border-none hover:bg-gray-100': ! disabled && variant === 'basic',
                'text-black border-black hover:text-white hover:border-neutral-800 hover:bg-neutral-800': ! disabled && variant === 'outline',
                'py-1 px-3 text-md whitespace-nowrap': size === 'small',
                'py-2 sm:py-3 px-6 sm:px-8 text-base sm:text-xl': size === 'medium',
                'py-4 px-12 text-2xl': size === 'large',
                'bg-gray-100 text-gray-300 border-gray-100 cursor-default': disabled,
                [className]: Boolean(className)
            })}
            onClick={onClick}
            disabled={disabled}
        >
            <motion.span className='font-bold text-center leading-10' variants={motionLabel}>
                {label}
            </motion.span>
            <motion.svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={3.5} stroke='currentColor' className={cx('w-4 h-4 sm:w-6 sm:h-6', {
                'hidden': ! animated
            })} variants={motionArrow}>
                <path strokeLinecap='round' strokeLinejoin='round' d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3' />
            </motion.svg>
        </motion.button>
    );

    if (href) {
        return (
            <Link href={href} className='inline-block'>
                {render}
            </Link>
        );
    }

    return render;
};

export { Button };